// @redux-policy: transient-display-cache
// userManagementThunks.js (loadUsers, loadCurrentUser, saveUser, etc.).
// All writes go through API endpoints. No localStorage persistence.
// Reviewed: 2026-02-19. See REDUX_VIOLATIONS.md V-P1-001.
import { createSlice, createSelector } from '@reduxjs/toolkit';

// User categories
export const USER_CATEGORIES = {
  CUSTOMER: 'customer',
  REFERRAL: 'referral',
  PLATFORM_USER: 'platform_user'
};

// Roles for platform users
export const PLATFORM_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  ANALYST: 'analyst',
  VIEWER: 'viewer'
};

// Permissions mapping
export const PERMISSIONS = {
  // Admin: full access
  admin: {
    canViewUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canViewCompanies: true,
    canEditCompanies: true,
    canDeleteCompanies: true,
    canViewTradePartners: true,
    canEditTradePartners: true,
    canDeleteTradePartners: true,
    canViewRisk: true,
    canEditRisk: true,
    canViewInvoices: true,
    canEditInvoices: true,
    canViewTermsheets: true,
    canEditTermsheets: true,
    canManageSettings: true,
    canViewDataExplorer: true,
    canEditDataExplorer: true,
    canViewFinancing: true
  },
  // Manager: can view and edit, but not delete
  manager: {
    canViewUsers: true,
    canEditUsers: true,
    canDeleteUsers: false,
    canViewCompanies: true,
    canEditCompanies: true,
    canDeleteCompanies: false,
    canViewTradePartners: true,
    canEditTradePartners: true,
    canDeleteTradePartners: false,
    canViewRisk: true,
    canEditRisk: true,
    canViewInvoices: true,
    canEditInvoices: true,
    canViewTermsheets: true,
    canEditTermsheets: true,
    canManageSettings: false,
    canViewDataExplorer: true,
    canEditDataExplorer: true
  },
  // Analyst: can view and edit specific areas
  analyst: {
    canViewUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canViewCompanies: true,
    canEditCompanies: false,
    canDeleteCompanies: false,
    canViewTradePartners: true,
    canEditTradePartners: false,
    canDeleteTradePartners: false,
    canViewRisk: true,
    canEditRisk: true,
    canViewInvoices: true,
    canEditInvoices: false,
    canViewTermsheets: true,
    canEditTermsheets: false,
    canManageSettings: false,
    canViewDataExplorer: true,
    canEditDataExplorer: false
  },
  // Viewer: read-only access
  viewer: {
    canViewUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canViewCompanies: true,
    canEditCompanies: false,
    canDeleteCompanies: false,
    canViewTradePartners: true,
    canEditTradePartners: false,
    canDeleteTradePartners: false,
    canViewRisk: true,
    canEditRisk: false,
    canViewInvoices: true,
    canEditInvoices: false,
    canViewTermsheets: true,
    canEditTermsheets: false,
    canManageSettings: false,
    canViewDataExplorer: true,
    canEditDataExplorer: false
  },
  // Customer: limited access to their own data
  customer: {
    canViewUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canViewCompanies: true,
    canEditCompanies: true,
    canDeleteCompanies: false,
    canViewTradePartners: true,
    canEditTradePartners: true,
    canDeleteTradePartners: false,
    canViewRisk: true,
    canEditRisk: false,
    canViewInvoices: true,
    canEditInvoices: true,
    canViewTermsheets: true,
    canEditTermsheets: false,
    canManageSettings: false,
    canViewDataExplorer: false,
    canEditDataExplorer: false
  },
  // Referral: very limited access
  referral: {
    canViewUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canViewCompanies: true,
    canEditCompanies: false,
    canDeleteCompanies: false,
    canViewTradePartners: false,
    canEditTradePartners: false,
    canDeleteTradePartners: false,
    canViewRisk: false,
    canEditRisk: false,
    canViewInvoices: false,
    canEditInvoices: false,
    canViewTermsheets: false,
    canEditTermsheets: false,
    canManageSettings: false,
    canViewDataExplorer: false,
    canEditDataExplorer: false
  }
};

const initialState = {
  users: [],
  currentUser: null,
  filters: {
    category: 'all',
    role: 'all',
    status: 'all',
    searchTerm: ''
  },
  loading: false,
  error: null
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    // Add a new user
    addUser: (state, action) => {
      const newUser = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        ...action.payload,
        status: action.payload.status || 'active'
      };
      state.users.push(newUser);
    },

    // Update an existing user
    updateUser: (state, action) => {
      const index = state.users.findIndex(user => user.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = {
          ...state.users[index],
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Delete a user
    deleteUser: (state, action) => {
      state.users = state.users.filter(user => user.id !== action.payload);
    },

    // Set current user (logged in user)
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },

    // Load all users
    setUsers: (state, action) => {
      state.users = action.payload;
    },

    // Update filters
    setFilter: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
    },

    // Toggle user status
    toggleUserStatus: (state, action) => {
      const user = state.users.find(u => u.id === action.payload);
      if (user) {
        user.status = user.status === 'active' ? 'inactive' : 'active';
        user.updatedAt = new Date().toISOString();
      }
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    }
  }
});

// Selectors
export const selectAllUsers = (state) => state.userManagement.users;
const selectFilters = (state) => state.userManagement.filters;

export const selectFilteredUsers = createSelector(
  [selectAllUsers, selectFilters],
  (users, filters) => users.filter((user) => {
    // Filter by category
    if (filters.category !== 'all' && user.category !== filters.category) {
      return false;
    }

    // Filter by role (for platform users)
    if (filters.role !== 'all' && user.role !== filters.role) {
      return false;
    }

    // Filter by status
    if (filters.status !== 'all' && user.status !== filters.status) {
      return false;
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const nameMatch = (user.firstName + ' ' + user.lastName).toLowerCase().includes(searchLower);
      const emailMatch = user.email?.toLowerCase().includes(searchLower);
      const companyMatch = user.companyName?.toLowerCase().includes(searchLower);

      if (!nameMatch && !emailMatch && !companyMatch) {
        return false;
      }
    }

    return true;
  })
);

export const selectCurrentUser = (state) => state.userManagement.currentUser;

export const selectUserPermissions = (state) => {
  const user = state.userManagement.currentUser;
  if (!user) return PERMISSIONS.viewer; // Default to viewer if no user

  // For platform users, use their role
  if (user.category === USER_CATEGORIES.PLATFORM_USER || user.category === 'platform') {
    // Force admin permissions for the demo user
    if (user.email === 'platform@demo.com') {
      return PERMISSIONS.admin;
    }
    return PERMISSIONS[user.role] || PERMISSIONS.viewer;
  }

  // For customers and referrals, use their category
  return PERMISSIONS[user.category] || PERMISSIONS.viewer;
};

export const {
  addUser,
  updateUser,
  deleteUser,
  setCurrentUser,
  setUsers,
  setFilter,
  toggleUserStatus,
  setLoading,
  setError,
  clearError
} = userManagementSlice.actions;

export default userManagementSlice.reducer;
